import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import Calendar from './Calendar/Calendar';
import { dateToISOFormat, hasValue } from '@utils';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { isValid } from 'date-fns';
import { UXPContext } from "@core/context/Context";
import { Dropdown } from '@widget/popups/Dropdown';
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import Input from '../Input';
/**
 *
 * @export
 *
 * This component is used to select a date.
 *
 * @example
 * ```
 *  <DatePicker
 *    title="Date"
 *    date={date}
 *    onChange={(date) => setDate(date)}
 * />
 * ```
 *
 * @example
 * <DatePicker
 *     title="Date"
 *     date={date}
 *     onChange={(date) => setDate(date)}
 *     options={{
 *         disableWeekEnds: true
 *     }}
 * />
 *
 * @category Inputs/Date and Time
 */
const DatePicker = (props) => {
    const context = React.useContext(UXPContext);
    // props
    let { title = context.$L('uxp-core.text.select-a-date'), date, onChange, closeOnSelect, options, disableInput, hideInput, hideLabels, placeholder, showFullMonthName = true, dropdownClassname, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, onClear } = props;
    if (!hasValue(placeholder))
        placeholder = context.$L('uxp-core.text.select-a-date');
    // states
    let [showPicker, setShowPicker] = React.useState(false);
    let [value, setValue] = React.useState(date);
    const [isFocused, setIsFocused] = React.useState(false);
    const [showInput, setShowInput] = React.useState(false);
    const triggerRef = React.useRef(null);
    React.useEffect(() => {
        setValue(date);
    }, [date]);
    const closePicker = () => {
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    // Handle focus to show focus styles
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    // Handle blur to remove focus styles
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    const onChangeHandler = (range) => {
        onChange(range.start);
        if (closeOnSelect) {
            setShowPicker(false);
        }
    };
    const handleInputBlur = (val) => {
        let _date = new Date(val);
        if (isValid(_date)) {
            onChange(_date);
        }
        else {
            onChange(null);
        }
        // Don't close on blur - user can click Done button or click outside to close
    };
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_datepicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_datepicker__header", children: [_jsx("div", { className: "uxpcore_datepicker__title", children: showInput ? context.$L('uxp-core.text.enter-date') : title }), !hideInput && (_jsx("div", { className: "uxpcore_datepicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-calendar') : context.$L('uxp-core.text.type-date'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'calendar' : 'keyboard'] }) }))] }), showInput && !hideInput && (_jsx("div", { className: "uxpcore_datepicker__input-container", children: _jsx(Input, { type: "text", value: typeof value == "string" ? value : dateToISOFormat(value), onChange: (val) => setValue(val), onBlur: handleInputBlur, readOnly: disableInput, placeholder: context.$L('uxp-core.text.enter-date'), fullWidth: true, hideClear: true }) })), !showInput && (_jsx(Calendar, Object.assign({ range: { start: date, end: null }, onSelect: (range) => onChangeHandler(range) }, options, { size: "small", showFullMonthName: showFullMonthName }))), (!closeOnSelect || showInput) && (_jsx("div", { className: "uxpcore_datepicker__footer", children: _jsx("button", { className: "uxpcore_datepicker__done-btn", onClick: closePicker, children: context.$L('uxp-core.text.done') }) }))] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = (!!date && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(null); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    // return
    return (_jsxs("div", { className: classNames("uxpcore_datepicker", { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--datepicker", stateClass, {
                    "uxpcore_input--has-prefix": !hideLabels,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": !!date,
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [!hideLabels && (_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'calendar'] }) })), _jsx("div", { className: "uxpcore_input__value", children: date
                            ? (typeof date == "string" ? date : dateToISOFormat(date))
                            : placeholder }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_datepicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: '20rem', minWidth: "20rem" })] }));
};
export default DatePicker;
